import { useState, useEffect, useRef } from "react";
import { styled } from "styled-components";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  ClockIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { stripePromise } from "../../lib/stripe";
import { useCart } from "../../contexts";
import {
  cancelOrder,
  createOrder,
  getMyQueuePosition,
  QueuePositionResponse,
} from "../../services/orderService";
import { OutlineOrangeButton, StyledButton } from "../../components/Typography";

const StepHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
`;

const StepEyebrow = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
`;

const StepTitle = styled.h2`
  margin: 0;
  font-family: "Sofia Pro", sans-serif;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const StepText = styled.p`
  margin: 0;
  max-width: 720px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 15px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.blue500};
`;

const PaymentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const TimerCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 20px;
  background:
    linear-gradient(180deg, rgba(246, 163, 28, 0.1) 0%, rgba(246, 163, 28, 0.02) 100%),
    ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => `${theme.colors.primary}55`};
`;

const TimerCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const TimerLabel = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${({ theme }) => theme.colors.grey};
`;

const TimerValue = styled.div<{ $urgent?: boolean }>`
  font-family: "Sofia Pro", sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme, $urgent }) => ($urgent ? theme.colors.error : theme.colors.blue900)};
`;

const StateCard = styled.div`
  padding: 24px;
  border-radius: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.white};
`;

const StateIcon = styled.div<{ $tone?: "primary" | "error" }>`
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  background: ${({ theme, $tone }) =>
    $tone === "error" ? "rgba(255, 77, 79, 0.12)" : `${theme.colors.primary}22`};
  color: ${({ theme, $tone }) => ($tone === "error" ? theme.colors.error : theme.colors.primary)};
`;

const StateTitle = styled.h3`
  margin: 0 0 8px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};
`;

const StateText = styled.p`
  margin: 0;
  font-family: "Sofia Pro", sans-serif;
  font-size: 15px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.blue500};
`;

const SecureList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 18px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const SecureCard = styled.div`
  padding: 14px 16px;
  border-radius: 18px;
  background: ${({ theme }) => theme.colors.grey100};
  font-family: "Sofia Pro", sans-serif;
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.blue500};
`;

const SecureTitle = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};
  margin-bottom: 4px;
`;

const PaymentFormShell = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PaymentFieldCard = styled.div`
  padding: 22px;
  border-radius: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background:
    linear-gradient(180deg, rgba(246, 163, 28, 0.06) 0%, rgba(246, 163, 28, 0) 100%),
    ${({ theme }) => theme.colors.white};
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-family: "Sofia Pro", sans-serif;
  font-size: 14px;
  line-height: 1.6;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;

  @media (max-width: 767px) {
    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const QUEUE_POLL_INTERVAL_MS = 3000;

const CheckoutTimer = () => {
  const { checkoutDeadline } = useCart();
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!checkoutDeadline) return;
    const tick = () => setTimeLeft(Math.max(0, checkoutDeadline - Date.now()));
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [checkoutDeadline]);

  if (!checkoutDeadline) return null;

  const minutes = Math.floor(timeLeft / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);

  return (
    <TimerCard>
      <StateIcon>
        <ClockIcon style={{ width: 24, height: 24 }} />
      </StateIcon>
      <TimerCopy>
        <TimerLabel>Zbývající čas na dokončení platby</TimerLabel>
        <TimerValue $urgent={timeLeft < 60000}>
          {minutes}:{seconds.toString().padStart(2, "0")}
        </TimerValue>
      </TimerCopy>
    </TimerCard>
  );
};

interface StripePaymentFormProps {
  onPaymentComplete: () => void;
}

const StripePaymentForm = ({ onPaymentComplete }: StripePaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setPaymentError(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.origin + "/shopping-cart" },
      redirect: "if_required",
    });

    if (error) {
      setPaymentError(error.message || "Platba se nezdařila. Zkuste to prosím znovu.");
      setIsProcessing(false);
      return;
    }

    onPaymentComplete();
  };

  return (
    <PaymentFormShell onSubmit={handleSubmit}>
      <PaymentFieldCard>
        <PaymentElement />
      </PaymentFieldCard>
      {paymentError && <ErrorMessage>{paymentError}</ErrorMessage>}
      <ActionRow>
        <StyledButton type="submit" disabled={!stripe || isProcessing}>
          {isProcessing ? "Zpracovávám platbu..." : "Zaplatit a dokončit"}
        </StyledButton>
      </ActionRow>
    </PaymentFormShell>
  );
};

interface ThirdProps {
  onPaymentComplete: () => void;
}

/**
 * Step 3: order creation + Stripe payment.
 *
 * Flow:
 *   1. Mount → call /Order/api/v1/create with the form data we collected in
 *      step 2. BE either gives us status=AwaitingPayment + clientSecret (if
 *      we got the auction's payment slot immediately) or status=Queued (if
 *      another buyer holds the slot — see "wait your turn" UX in BE
 *      Modules/Orders).
 *   2. While Queued, poll /api/v1/orders/me/queue-position every ~3s until
 *      our turn comes. The BE's OrderAbandonmentSweeper guarantees the
 *      slot frees within SlotHoldSeconds (default 90s) if the holder bails.
 *   3. Once status flips to AwaitingPayment, render Stripe PaymentElement
 *      against the clientSecret. confirmPayment finalises the charge;
 *      Stripe webhook on the BE flips Order.Status to Paid (with
 *      AuctionShareLink Used as a side-effect if applicable).
 */
const Third = ({ onPaymentComplete }: ThirdProps) => {
  const {
    cartProduct,
    buyerInfo,
    sameAddress,
    billingInfo,
    orderId,
    paymentIntentClientSecret,
    setPaymentIntentClientSecret,
    setOrderId,
    checkoutDeadline,
    setCheckoutStep,
  } = useCart();

  const [initError, setInitError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [queueState, setQueueState] = useState<QueuePositionResponse | null>(null);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const hasStartedInitialization = useRef(false);
  const hasCancelledExpiredOrder = useRef(false);
  const isCheckoutExpired = !!checkoutDeadline && checkoutDeadline <= currentTime;

  // ─── Local timer tick for the displayed countdown ─────────────────────────
  useEffect(() => {
    if (!checkoutDeadline) return;
    const interval = window.setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [checkoutDeadline]);

  // ─── If our local checkout timer expired, cancel the BE order too ─────────
  useEffect(() => {
    if (!isCheckoutExpired || !orderId || hasCancelledExpiredOrder.current) return;
    hasCancelledExpiredOrder.current = true;
    void cancelOrder(orderId).catch(() => { /* local expiration UX continues */ });
  }, [isCheckoutExpired, orderId]);

  // ─── Step entry: create the order ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const initOrder = async () => {
      if (hasStartedInitialization.current) return;
      if (paymentIntentClientSecret || isCheckoutExpired) return;
      if (!cartProduct?.auctionId || !buyerInfo) return;
      if (!stripePromise) return;

      hasStartedInitialization.current = true;
      setIsInitializing(true);
      setInitError(null);

      try {
        const order = await createOrder({
          auctionId: cartProduct.auctionId,
          buyerName: buyerInfo.name,
          buyerSurname: buyerInfo.surname,
          buyerEmail: buyerInfo.email,
          buyerPhone: buyerInfo.phone,
          deliveryStreet: buyerInfo.street,
          deliveryAptNumber: buyerInfo.aptNumber,
          deliveryCity: buyerInfo.city,
          deliveryZip: buyerInfo.zip,
          billingStreet: sameAddress ? buyerInfo.street : billingInfo?.street,
          billingAptNumber: sameAddress ? buyerInfo.aptNumber : billingInfo?.aptNumber,
          billingCity: sameAddress ? buyerInfo.city : billingInfo?.city,
          billingZip: sameAddress ? buyerInfo.zip : billingInfo?.zip,
          sameAddress,
        });

        if (cancelled) return;

        setOrderId(order.orderId);
        if (order.status === "AwaitingPayment" && order.clientSecret) {
          setPaymentIntentClientSecret(order.clientSecret);
        } else {
          // We're behind someone in the queue — start polling.
          setQueueState({
            auctionId: cartProduct.auctionId,
            status: order.status,
            position: order.queuePosition,
            orderId: order.orderId,
          });
        }
      } catch {
        if (!cancelled)
          setInitError("Nepodařilo se vytvořit objednávku. Zkuste se vrátit o krok zpět a pokračovat znovu.");
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    };

    void initOrder();
    return () => { cancelled = true; };
  }, [
    billingInfo, buyerInfo, cartProduct, isCheckoutExpired,
    paymentIntentClientSecret, sameAddress, setOrderId, setPaymentIntentClientSecret,
  ]);

  // ─── Queue polling: only active while we're Queued, drops once promoted ───
  useEffect(() => {
    if (!cartProduct?.auctionId) return;
    if (paymentIntentClientSecret) return;
    if (isCheckoutExpired) return;
    if (queueState?.status !== "Queued") return;

    let cancelled = false;
    const auctionId = cartProduct.auctionId;

    const poll = async () => {
      try {
        const next = await getMyQueuePosition(auctionId);
        if (cancelled) return;
        setQueueState(next);
        if (next.status === "AwaitingPayment" && next.clientSecret) {
          setPaymentIntentClientSecret(next.clientSecret);
        }
      } catch { /* transient — next tick will retry */ }
    };

    const interval = window.setInterval(poll, QUEUE_POLL_INTERVAL_MS);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [cartProduct?.auctionId, queueState?.status, paymentIntentClientSecret,
      isCheckoutExpired, setPaymentIntentClientSecret]);

  const goBackToDetails = () => setCheckoutStep(1);
  const isQueued = queueState?.status === "Queued" && !paymentIntentClientSecret;

  return (
    <PaymentStack>
      <StepHeader>
        <StepEyebrow>Krok 3</StepEyebrow>
        <StepTitle>Platba a potvrzení objednávky</StepTitle>
        <StepText>
          V této části dokončíte objednávku. Pokud aktuálně platí jiný kupující, počkáte si a po
          dokončení jejich platby budete automaticky promotováni na řadu.
        </StepText>
      </StepHeader>

      <CheckoutTimer />

      {isCheckoutExpired && (
        <StateCard>
          <StateIcon $tone="error">
            <ExclamationTriangleIcon style={{ width: 24, height: 24 }} />
          </StateIcon>
          <StateTitle>Čas pro dokončení platby vypršel</StateTitle>
          <StateText>
            Vraťte se o krok zpět, potvrďte objednávkové údaje znovu a tím obnovíte časový limit.
          </StateText>
          <ActionRow style={{ marginTop: 20 }}>
            <OutlineOrangeButton onClick={goBackToDetails}>
              Vrátit se k údajům
            </OutlineOrangeButton>
          </ActionRow>
        </StateCard>
      )}

      {!isCheckoutExpired && !stripePromise && (
        <StateCard>
          <StateIcon>
            <CreditCardIcon style={{ width: 24, height: 24 }} />
          </StateIcon>
          <StateTitle>Platební brána není v lokálním prostředí připravená</StateTitle>
          <StateText>
            V produkčním prostředí se zde zobrazí Stripe formulář. Pro lokální aktivaci je potřeba
            nastavit proměnnou <strong>REACT_APP_STRIPE_PUBLISHABLE_KEY</strong>.
          </StateText>
        </StateCard>
      )}

      {!isCheckoutExpired && stripePromise && isInitializing && (
        <StateCard>
          <StateIcon>
            <ShieldCheckIcon style={{ width: 24, height: 24 }} />
          </StateIcon>
          <StateTitle>Připravujeme bezpečnou platbu</StateTitle>
          <StateText>Ověřujeme objednávku a připravujeme platební formulář.</StateText>
        </StateCard>
      )}

      {!isCheckoutExpired && stripePromise && initError && (
        <StateCard>
          <StateIcon $tone="error">
            <ExclamationTriangleIcon style={{ width: 24, height: 24 }} />
          </StateIcon>
          <StateTitle>Něco se pokazilo při přípravě platby</StateTitle>
          <StateText>{initError}</StateText>
        </StateCard>
      )}

      {!isCheckoutExpired && stripePromise && isQueued && (
        <StateCard>
          <StateIcon>
            <UsersIcon style={{ width: 24, height: 24 }} />
          </StateIcon>
          <StateTitle>Jste ve frontě na platbu</StateTitle>
          <StateText>
            Aktuálně platí jiný kupující. Vaše pořadí ve frontě:{" "}
            <strong>{queueState ? queueState.position + 1 : "—"}</strong>. Jakmile na vás dojde řada,
            zobrazí se platební formulář automaticky.
          </StateText>
          <SecureList>
            <SecureCard>
              <SecureTitle>Co se děje</SecureTitle>
              Aktivní kupující má 90 sekund na dokončení platby. Pokud nedokončí, automaticky
              postupujete na další pozici.
            </SecureCard>
            <SecureCard>
              <SecureTitle>Můžete zavřít stránku?</SecureTitle>
              Lépe vyčkejte — vaše místo ve frontě se ztratí, pokud objednávku zrušíte.
            </SecureCard>
          </SecureList>
        </StateCard>
      )}

      {!isCheckoutExpired && stripePromise && paymentIntentClientSecret && (
        <StateCard>
          <StateIcon>
            <ShieldCheckIcon style={{ width: 24, height: 24 }} />
          </StateIcon>
          <StateTitle>Bezpečná platba kartou</StateTitle>
          <StateText>
            Platební formulář je zabezpečený přes Stripe. Údaje o kartě se nezpracovávají přímo v aplikaci.
          </StateText>
          <div style={{ marginTop: 22 }}>
            <Elements stripe={stripePromise} options={{ clientSecret: paymentIntentClientSecret }}>
              <StripePaymentForm onPaymentComplete={onPaymentComplete} />
            </Elements>
          </div>
        </StateCard>
      )}
    </PaymentStack>
  );
};

export default Third;
